import { CurrentSchoolYear } from 'Src/ng2/shared/constants/current-school-year.constant';
import { IWindowService } from 'angular';
import { IStateParamsService } from 'angular-ui-router';
import { IStateService } from 'angular-ui-router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CreditsRoute, SettingsRoute, SupportsRoute } from '../../../ng2/shared/constants/settings-routes.constant';
import { USER_ROLE_PERMISSIONS_FOR_STATES } from '../../../ng2/shared/constants/user-role-permissions-for-states.constant';
import { ImSchool } from '../../../ng2/shared/services/im-models/im-school';
import { ISchool } from '../../../ng2/shared/typings/interfaces/school.interface';
import { SpinnerModal } from '../../shared/modals/status-spinner/status-spinner.service';
import { IStateGoOpts } from './../../../ng2/shared/services/prev-state/prev-state.service';
import { LoadActivities } from './../../../ng2/store/actions/activities-actions';
import { LoadSchoolAssessments } from './../../../ng2/store/actions/assessments-actions/school-assessments-actions';
import {
  getActivitiesEntitiesList,
  getActivitiesLoadedStatus,
} from './../../../ng2/store/selectors/activities-selectors';
import {
  getSchoolAssessmentsEntitiesList,
  getSchoolAssessmentsLoadedStatus,
} from './../../../ng2/store/selectors/assessments-selectors/school-assessments-selectors';
import { CreateSupportCtrl } from './../../features/supports/create-support/create-support.controller';

angular.module('nv.routes.main').config($stateProvider => {
  /* istanbul ignore next */
  $stateProvider
    .state('main.school.settings', {
      abstract: true,
      url: '/settings',
      template: '<div ui-view></div>',
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.settings'],
      },
      onExit: ($window: IWindowService) => {
        $window.document.title = 'Data Portal';
      },
    })

    .state('main.school.settings.metrics', {
      abstract: true,
      url: '/metrics',
      resolve: {
        settingsRoute: () => {
          return SettingsRoute.Metrics;
        },
      },
      template: '<div ui-view></div>',
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.settings.metrics-and-master-program'],
      },
      onEnter: (
        $state: IStateService,
        $window: IWindowService,
        ErrorModal,
        ImSchool: ImSchool,
        SpinnerModal: SpinnerModal,
        currentSchool: ISchool,
      ) => {
        $state.transition.then(() => {
          const isHighSchool = ImSchool.isHighSchool(currentSchool);
          if (!isHighSchool) {
            const title = 'Unauthorized Access';
            const err = new Error('Your school cannot access this page.');
            $state.go('main.school.settings.supports.all-supports').then(() => {
              ErrorModal.show(title, err);
            });
          } else {
            $window.document.title = 'Settings - Metrics | Data Portal';
            SpinnerModal.deactivateSpinner();
          }
        });
      },
    })

    .state('main.school.settings.metrics.distance-to-grad', {
      url: '/distance-to-grad',
      template: `
            <settings
              [current-school]=$resolve.currentSchool
              [current-user]=$resolve.currentUser
              [settings-route]=$resolve.settingsRoute
            ></settings>
          `,
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.settings.metrics-and-master-program'],
      },
    })

    .state('main.school.settings.metrics.credits', {
      url: '/credits',
      resolve: {
        creditsRoute: () => {
          return CreditsRoute.Credits;
        },
      },
      template: `
            <settings
              [credits-route]=$resolve.creditsRoute
              [current-school]=$resolve.currentSchool
              [current-user]=$resolve.currentUser
              [settings-route]=$resolve.settingsRoute
            ></settings>
          `,
    })

    .state('main.school.settings.metrics.regents', {
      url: '/regents',
      resolve: {
        creditsRoute: () => {
          return CreditsRoute.Regents;
        },
      },
      template: `
            <settings
              [credits-route]=$resolve.creditsRoute
              [current-school]=$resolve.currentSchool
              [current-user]=$resolve.currentUser
              [settings-route]=$resolve.settingsRoute
            ></settings>
          `,

      onEnter: (
        $state: IStateService,
        $window: IWindowService,
        ErrorModal,
        ImSchool: ImSchool,
        SpinnerModal: SpinnerModal,
        currentSchool: ISchool,
      ) => {
        $state.transition.then(() => {
          const isPbatSchool = ImSchool.isPbatSchool(currentSchool);
          if (isPbatSchool) {
            const title = 'Unauthorized Access';
            const err = new Error('Your school cannot access this page.');
            $state.go('main.school.home').then(() => {
              ErrorModal.show(title, err);
            });
          }
        });
      },
    })

    .state('main.school.settings.metrics.grad-planning', {
      abstract: true,
      url: '/grad-planning',
      resolve: {
        creditsRoute: () => {
          return CreditsRoute.GradPlanning;
        },
      },
      template: '<div ui-view></div>',
    })

    .state('main.school.settings.metrics.grad-planning.fourth-year-students', {
      url: '/fourth-year-students',
      resolve: {
        gradPlanRoute: () => {
          return 'fourth';
        },
      },
      template: `
            <settings
              [credits-route]=$resolve.creditsRoute
              [current-school]=$resolve.currentSchool
              [current-user]=$resolve.currentUser
              [grad-plan-route]=$resolve.gradPlanRoute
              [settings-route]=$resolve.settingsRoute
            ></settings>
          `,
    })

    .state('main.school.settings.metrics.grad-planning.third-year-students', {
      url: '/third-year-students',
      resolve: {
        gradPlanRoute: () => {
          return 'third';
        },
      },
      template: `
            <settings
              [credits-route]=$resolve.creditsRoute
              [current-school]=$resolve.currentSchool
              [current-user]=$resolve.currentUser
              [grad-plan-route]=$resolve.gradPlanRoute
              [settings-route]=$resolve.settingsRoute
            ></settings>
          `,
    })

    .state('main.school.settings.metrics.grad-planning.second-year-students', {
      url: '/second-year-students',
      resolve: {
        gradPlanRoute: () => {
          return 'second';
        },
      },
      template: `
            <settings
              [credits-route]=$resolve.creditsRoute
              [current-school]=$resolve.currentSchool
              [current-user]=$resolve.currentUser
              [grad-plan-route]=$resolve.gradPlanRoute
              [settings-route]=$resolve.settingsRoute
            ></settings>
          `,
    })

    .state('main.school.settings.metrics.grad-planning.first-year-students', {
      url: '/first-year-students',
      resolve: {
        gradPlanRoute: () => {
          return 'first';
        },
      },
      template: `
            <settings
              [credits-route]=$resolve.creditsRoute
              [current-school]=$resolve.currentSchool
              [current-user]=$resolve.currentUser
              [grad-plan-route]=$resolve.gradPlanRoute
              [settings-route]=$resolve.settingsRoute
            ></settings>
          `,
    })

    .state('main.school.settings.master-program', {
      url: '/master-program',
      resolve: {
        settingsRoute: () => {
          return SettingsRoute.MasterProgram;
        },
      },
      template: `
            <settings
              [current-school]=$resolve.currentSchool
              [current-user]=$resolve.currentUser
              [settings-route]=$resolve.settingsRoute
            ></settings>
          `,
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.settings.metrics-and-master-program'],
      },
      onEnter: (
        $state: IStateService,
        $window: IWindowService,
        ErrorModal,
        ImSchool: ImSchool,
        SpinnerModal: SpinnerModal,
        currentSchool: ISchool,
      ) => {
        $state.transition.then(() => {
          const isHighSchool = ImSchool.isHighSchool(currentSchool);
          if (!isHighSchool) {
            const title = 'Unauthorized Access';
            const err = new Error('Your school cannot access this page.');
            $state.go('main.school.settings.supports.all-supports').then(() => {
              ErrorModal.show(title, err);
            });
          } else {
            $window.document.title = 'Settings - Master program | Data Portal';
            SpinnerModal.deactivateSpinner();
          }
        });
      },
    })

    .state('main.school.settings.supports', {
      abstract: true,
      url: '/supports',
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.settings.supports'],
      },
      resolve: {
        settingsRoute: () => {
          return SettingsRoute.Supports;
        },
        currentSupports: [
          'Support',
          'currentSchool',
          (Support, currentSchool) => {
            return Support.query({
              schoolId: currentSchool._id,
            }).$promise;
          },
        ],
        schoolSupports: [
          'Support',
          'currentSchool',
          'sdcActions',
          'UtilitiesService',
          (Support, currentSchool, sdcActions, UtilitiesService) => {
            const promise = Support.query({ schoolId: currentSchool._id, status: 'ACTIVE' }).$promise;
            return promise.then(data => {
              const supportsToStore = UtilitiesService.toPOJO(data);
              return sdcActions.setAllSchoolSupports(supportsToStore);
            });
          },
        ],
        studentSupports$: [
          'StudentSupportsResolver',
          'currentSchool',
          'Store',
          'sdcActions',
          'UtilitiesService',
          (StudentSupportsResolver, currentSchool, Store) => {
            return StudentSupportsResolver.resolveStudentSupports$(currentSchool, Store);
          },
        ],
      },
      template: '<div ui-view></div>',
      onEnter: (SpinnerModal: SpinnerModal, $window: IWindowService) => {
        $window.document.title = 'Settings - Supports | Data Portal';
        SpinnerModal.deactivateSpinner();
      },
    })

    .state('main.school.settings.supports.new', {
      url: '/new/:supportRequestOrigin',
      params: {
        category: null,
        goBackStateOpts: null,
        name: null,
        status: null,
        studentIds: null, // used for creating student supports on support creation (CM)
        supportId: 'new',
        supportRequestOrigin: null,
      },
      template: require('./../../features/supports/create-support/create-support.html'),
      controller: CreateSupportCtrl,
      controllerAs: 'ctrl',
      resolve: {
        currentSupport: [
          'Support',
          'currentSchool',
          '$stateParams',
          '$q',
          'UtilitiesService',
          'Rollbar',
          (Support, currentSchool, $stateParams: IStateParamsService, $q, UtilitiesService, Rollbar) => {
            function makeNewSupport() {
              let support = Support.createFromTemplate();
              support._id = UtilitiesService.createV4Uuid();
              return support;
            }
            return makeNewSupport();
          },
        ],
        resolvedSchoolUsers: [
          'User',
          '$stateParams',
          (User, $stateParams: IStateParamsService) => {
            const { schoolId } = $stateParams;
            return User.fetchUsersWithFullAccess({ schoolId }).$promise;
          },
        ],
        // `goBackStateOpts` is used to go back to ssv when comping from v3 assign supports panel (CM)
        goBackStateOpts: [
          '$stateParams',
          ($stateParams: IStateParamsService) => {
            const goBackStateOpts: IStateGoOpts = $stateParams.goBackStateOpts;
            return goBackStateOpts;
          },
        ],
        // `studentIds` is used when creating student supports on support creation
        // `studentIds` length of 1 -> creates one student supports
        // `studentIds` length > 1 -> bulk creates student supports
        studentIds: [
          '$stateParams',
          ($stateParams: IStateParamsService) => {
            const studentIds: IStateGoOpts = $stateParams.studentIds;
            return studentIds;
          },
        ],
        schoolSupports: [
          'Support',
          'currentSchool',
          'sdcActions',
          'UtilitiesService',
          (Support, currentSchool, sdcActions, UtilitiesService) => {
            const promise = Support.query({ schoolId: currentSchool._id, status: 'ACTIVE' }).$promise;
            return promise.then(data => {
              const supportsToStore = UtilitiesService.toPOJO(data);
              return sdcActions.setAllSchoolSupports(supportsToStore);
            });
          },
        ],
      },
    })

    .state('main.school.settings.supports.duplicate', {
      url: '/duplicate/{supportToDuplicateId}',
      params: {
        category: null,
        goBackStateOpts: null,
        name: null,
        status: null,
        studentIds: null, // used for creating student supports on support creation (CM)
        supportId: 'duplicate',
        supportRequestOrigin: null,
      },
      template: require('./../../features/supports/create-support/create-support.html'),
      controller: CreateSupportCtrl,
      controllerAs: 'ctrl',
      resolve: {
        currentSupport: [
          'Support',
          'currentSchool',
          '$stateParams',
          '$q',
          'UtilitiesService',
          'Rollbar',
          (Support, currentSchool, $stateParams: IStateParamsService, $q, UtilitiesService, Rollbar) => {
            const deferred = $q.defer();
            let duplicatedSupport = new Support({ _id: $stateParams.supportToDuplicateId });
            duplicatedSupport.$get(
              () => {
                duplicatedSupport._id = UtilitiesService.createV4Uuid();
                // Changing duplicated support status to Active.
                duplicatedSupport.status = 'ACTIVE';
                deferred.resolve(new Support(angular.copy(duplicatedSupport)));
              },
              err => deferred.reject(err),
            );
            return deferred.promise;
          },
        ],
        resolvedSchoolUsers: [
          'User',
          '$stateParams',
          (User, $stateParams: IStateParamsService) => {
            const { schoolId } = $stateParams;
            return User.fetchUsersWithFullAccess({ schoolId }).$promise;
          },
        ],
        // `goBackStateOpts` is used to go back to ssv when comping from v3 assign supports panel (CM)
        goBackStateOpts: [
          '$stateParams',
          ($stateParams: IStateParamsService) => {
            const goBackStateOpts: IStateGoOpts = $stateParams.goBackStateOpts;
            return goBackStateOpts;
          },
        ],
        // `studentIds` is used when creating student supports on support creation
        // `studentIds` length of 1 -> creates one student supports
        // `studentIds` length > 1 -> bulk creates student supports
        studentIds: [
          '$stateParams',
          ($stateParams: IStateParamsService) => {
            const studentIds: IStateGoOpts = $stateParams.studentIds;
            return studentIds;
          },
        ],
        schoolSupports: [
          'Support',
          'currentSchool',
          'sdcActions',
          'UtilitiesService',
          (Support, currentSchool, sdcActions, UtilitiesService) => {
            const promise = Support.query({ schoolId: currentSchool._id, status: 'ACTIVE' }).$promise;
            return promise.then(data => {
              const supportsToStore = UtilitiesService.toPOJO(data);
              return sdcActions.setAllSchoolSupports(supportsToStore);
            });
          },
        ],
      },
    })

    .state('main.school.settings.supports.edit', {
      url: '/edit/{supportId}',
      params: {
        supportRequestOrigin: null,
        supportToDuplicateId: null,
        status: null,
        category: null,
        name: null,
        goBackStateOpts: null,
        studentIds: null, // used for creating student supports on support creation (CM)
      },
      template: require('./../../features/supports/create-support/create-support.html'),
      controller: CreateSupportCtrl,
      controllerAs: 'ctrl',
      resolve: {
        currentSupport: [
          'Support',
          'currentSchool',
          '$stateParams',
          '$q',
          'UtilitiesService',
          'Rollbar',
          (Support, currentSchool, $stateParams: IStateParamsService, $q, UtilitiesService, Rollbar) => {
            function makeNewSupport() {
              // These state params can be provided a value by the assign support modal (CM).
              const { name, category } = $stateParams;
              let support = Support.createFromTemplate();
              support._id = UtilitiesService.createV4Uuid();

              if (name) support.name = name;
              if (category) {
                support.category = category;
                support.setMetaData({ category });
              }

              return support;
            }
            if ($stateParams.supportId) {
              return Support.get({
                id: $stateParams.supportId,
              }).$promise;
            } else {
              // Log debug message to Rollbar, but move forward as if
              // new support was requested (JC)
              const err = new Error('No supportId provided');
              Rollbar.debug(err);
              return makeNewSupport();
            }
          },
        ],
        resolvedSchoolUsers: [
          'User',
          '$stateParams',
          (User, $stateParams: IStateParamsService) => {
            const { schoolId } = $stateParams;
            return User.fetchUsersWithFullAccess({ schoolId }).$promise;
          },
        ],
        // `goBackStateOpts` is used to go back to ssv when comping from v3 assign supports panel (CM)
        goBackStateOpts: [
          '$stateParams',
          ($stateParams: IStateParamsService) => {
            const goBackStateOpts: IStateGoOpts = $stateParams.goBackStateOpts;
            return goBackStateOpts;
          },
        ],
        // `studentIds` is used when creating student supports on support creation
        // `studentIds` length of 1 -> creates one student supports
        // `studentIds` length > 1 -> bulk creates student supports
        studentIds: [
          '$stateParams',
          ($stateParams: IStateParamsService) => {
            const studentIds: IStateGoOpts = $stateParams.studentIds;
            return studentIds;
          },
        ],
        schoolSupports: [
          'Support',
          'currentSchool',
          'sdcActions',
          'UtilitiesService',
          (Support, currentSchool, sdcActions, UtilitiesService) => {
            const promise = Support.query({ schoolId: currentSchool._id, status: 'ACTIVE' }).$promise;
            return promise.then(data => {
              const supportsToStore = UtilitiesService.toPOJO(data);
              return sdcActions.setAllSchoolSupports(supportsToStore);
            });
          },
        ],
      },
    })

    .state('main.school.settings.supports.all-supports', {
      url: '/all-supports',
      resolve: {
        supportsRoute: () => {
          return SupportsRoute.AllSupports;
        },
        schoolSupports: [
          'Support',
          'currentSchool',
          'sdcActions',
          'UtilitiesService',
          (Support, currentSchool, sdcActions, UtilitiesService) => {
            const promise = Support.query({ schoolId: currentSchool._id, status: 'ACTIVE' }).$promise;
            return promise.then(data => {
              const supportsToStore = UtilitiesService.toPOJO(data);
              return sdcActions.setAllSchoolSupports(supportsToStore);
            });
          },
        ],
      },
      template: `
            <settings
              [current-school]=$resolve.currentSchool
              [current-supports]=$resolve.currentSupports
              [current-user]=$resolve.currentUser
              [settings-route]=$resolve.settingsRoute
              [student-supports$]=$resolve.studentSupports$
              [supports-route]=$resolve.supportsRoute
            ></settings>
          `,
      onEnter: (SpinnerModal: SpinnerModal) => {
        SpinnerModal.deactivateSpinner();
      },
    })

    .state('main.school.settings.supports.supports-roster', {
      url: '/support-rosters',
      resolve: {
        supportsRoute: () => {
          return SupportsRoute.SupportRosters;
        },
        attendanceRecords: [
          'AttendanceRecord',
          'currentSchool',
          (AttendanceRecord, currentSchool) => {
            return AttendanceRecord.query({
              schoolId: currentSchool._id,
            }).$promise;
          },
        ],
      },
      template: `
            <settings
              [attendance-records]=$resolve.attendanceRecords
              [current-school]=$resolve.currentSchool
              [current-supports]=$resolve.currentSupports
              [current-user]=$resolve.currentUser
              [settings-route]=$resolve.settingsRoute
              [student-supports$]=$resolve.studentSupports$
              [supports-route]=$resolve.supportsRoute
            ></settings>
          `,
    })

    .state('main.school.settings.supports.support-attendance', {
      url: '/support-attendance',
      resolve: {
        supportsRoute: () => {
          return SupportsRoute.SupportAttendance;
        },
        attendanceRecords: [
          'AttendanceRecord',
          'currentSchool',
          (AttendanceRecord, currentSchool) => {
            return AttendanceRecord.query({
              schoolId: currentSchool._id,
            }).$promise;
          },
        ],
      },
      template: `
            <settings
              [attendance-records]=$resolve.attendanceRecords
              [current-school]=$resolve.currentSchool
              [current-supports]=$resolve.currentSupports
              [current-user]=$resolve.currentUser
              [settings-route]=$resolve.settingsRoute
              [student-supports$]=$resolve.studentSupports$
              [supports-route]=$resolve.supportsRoute
            ></settings>
          `,
    })

    .state('main.school.settings.activities', {
      url: '/activities',
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.settings.activities'],
      },
      resolve: {
        settingsRoute: () => {
          return SettingsRoute.Activities;
        },
        activities$: [
          'Store',
          'currentSchool',
          (Store, currentSchool) => {
            const { _id } = currentSchool;
            const payload = { schoolId: _id };
            const activitiesLoaded = getActivitiesLoadedStatus;
            const destroy$: Subject<boolean> = new Subject<boolean>();

            return new Promise(resolve => {
              Store.select(activitiesLoaded)
                .pipe(takeUntil(destroy$))
                .subscribe(loaded => {
                  if (!loaded) {
                    Store.dispatch(new LoadActivities(payload));
                  } else {
                    const activities = getActivitiesEntitiesList;
                    const activities$ = Store.select(activities);
                    destroy$.next(true);
                    destroy$.unsubscribe();
                    resolve(activities$);
                  }
                });
            });
          },
        ],
      },
      template: `
            <settings
              [current-school]=$resolve.currentSchool
              [current-user]="$resolve.currentUser"
              [settings-route]="$resolve.settingsRoute"
              [activities$]="$resolve.activities$"
            ></settings>
          `,
      onEnter: (
        $state: IStateService,
        $window: IWindowService,
        ErrorModal,
        ImSchool: ImSchool,
        SpinnerModal: SpinnerModal,
        currentSchool: ISchool,
      ) => {
        $state.transition.then(() => {
          const isHighSchool = ImSchool.isHighSchool(currentSchool);
          if (!isHighSchool) {
            const title = 'Unauthorized Access';
            const err = new Error('Your school cannot access this page.');
            $state.go('main.school.settings.supports.all-supports').then(() => {
              ErrorModal.show(title, err);
            });
          } else {
            $window.document.title = 'Settings - Activities | Data Portal';
            SpinnerModal.deactivateSpinner();
          }
        });
      },
    })

    .state('main.school.settings.user-management', {
      url: '/user-management',
      resolve: {
        settingsRoute: () => {
          return SettingsRoute.UserManagement;
        },
        schoolUsers: [
          'User',
          '$stateParams',
          (User, $stateParams: IStateParamsService) => {
            const { schoolId } = $stateParams;
            const promise = User.fetchUsersWithFullAccess({ schoolId }).$promise;

            return promise.then(
              users => {
                return users;
              },
              function() {
                return undefined;
              },
            );
          },
        ],
        activities$: [
          'Store',
          'currentSchool',
          (Store, currentSchool) => {
            const { _id } = currentSchool;
            const payload = { schoolId: _id };
            const activitiesLoaded = getActivitiesLoadedStatus;
            const destroy$: Subject<boolean> = new Subject<boolean>();

            return new Promise(resolve => {
              Store.select(activitiesLoaded)
                .pipe(takeUntil(destroy$))
                .subscribe(loaded => {
                  if (!loaded) {
                    Store.dispatch(new LoadActivities(payload));
                  } else {
                    const activities = getActivitiesEntitiesList;
                    const activities$ = Store.select(activities);
                    destroy$.next(true);
                    destroy$.unsubscribe();
                    resolve(activities$);
                  }
                });
            });
          },
        ],
      },
      template: `
            <settings
              [current-user]="$resolve.currentUser"
              [current-school]=$resolve.currentSchool
              [settings-route]="$resolve.settingsRoute"
              [activities$]="$resolve.activities$"
              [school-users]="$resolve.schoolUsers"
            ></settings>
          `,
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.settings.user-management'],
      },
      onEnter: (SpinnerModal: SpinnerModal, $window) => {
        $window.document.title = 'Settings - User management | Data Portal';
        SpinnerModal.deactivateSpinner();
      },
    })

    .state('main.school.settings.assessments', {
      url: '/assessments',
      resolve: {
        settingsRoute: () => {
          return SettingsRoute.Assessments;
        },
        schoolUsers: [
          'User',
          '$stateParams',
          (User, $stateParams: IStateParamsService) => {
            const { schoolId } = $stateParams;
            const promise = User.fetchUsersWithFullAccess({ schoolId }).$promise;

            return promise.then(
              users => {
                return users;
              },
              function() {
                return undefined;
              },
            );
          },
        ],
        schoolAssessments$: [
          'Store',
          'currentSchool',
          (Store, currentSchool) => {
            const { _id } = currentSchool;
            const payload = { schoolId: _id, schoolYear: CurrentSchoolYear.WITH_SY_PREFIX };
            const schoolAssessmentsLoaded = getSchoolAssessmentsLoadedStatus;
            const destroy$: Subject<boolean> = new Subject<boolean>();

            return new Promise(resolve => {
              Store.select(schoolAssessmentsLoaded)
                .pipe(takeUntil(destroy$))
                .subscribe(loaded => {
                  if (!loaded) {
                    Store.dispatch(new LoadSchoolAssessments(payload));
                  } else {
                    const schoolAssessments = getSchoolAssessmentsEntitiesList;
                    const schoolAssessments$ = Store.select(schoolAssessments);
                    destroy$.next(true);
                    destroy$.unsubscribe();
                    resolve(schoolAssessments$);
                  }
                });
            });
          },
        ],
      },
      template: `
            <settings
              [current-user]="$resolve.currentUser"
              [current-school]=$resolve.currentSchool
              [settings-route]="$resolve.settingsRoute"
              [school-assessments$]="$resolve.schoolAssessments$"
            ></settings>
          `,
      data: {
        roles: USER_ROLE_PERMISSIONS_FOR_STATES['main.school.settings.assessments'],
      },
      onEnter: (
        SpinnerModal: SpinnerModal,
        $window,
        $state: IStateService,
        currentSchool: ISchool,
        ImSchool: ImSchool,
        ErrorModal,
      ) => {
        $state.transition.then(() => {
          const isHighSchool = ImSchool.isHighSchool(currentSchool);
          const isHybridSchool = ImSchool.isHybridSchool(currentSchool);
          const validSchoolType = !isHighSchool || isHybridSchool;
          if (!validSchoolType) {
            const title = 'Unauthorized Access';
            const err = new Error('Your school cannot access this page.');
            $state.go('main.school.settings.supports.all-supports').then(() => {
              ErrorModal.show(title, err);
            });
          }
          $window.document.title = 'Settings - Assessments | Data Portal';
          SpinnerModal.deactivateSpinner();
        });
      },
    });
});
